import { useOutletContext } from 'react-router';
import { VStack } from '@astryxdesign/core/VStack';
import { HStack } from '@astryxdesign/core/HStack';
import { Heading } from '@astryxdesign/core/Heading';
import { Text } from '@astryxdesign/core/Text';
import { Card } from '@astryxdesign/core/Card';
import { Button } from '@astryxdesign/core/Button';
import { Grid } from '@astryxdesign/core/Grid';
import { Badge } from '@astryxdesign/core/Badge';
import { List, ListItem } from '@astryxdesign/core/List';
import {
  CalendarPlus, Car, Clock, Drop, MapPin, Medal, Phone, ShieldCheck, Sparkle, Star, Wrench, NavigationArrow,
} from '@phosphor-icons/react';
import { useTenant } from '../../app/tenant-context';
import { imageUrl } from '../../lib/public-api';
import { Reveal } from '../../components/Reveal';
import { formatDuration, formatMoney, WEEKDAY_NAMES } from '../../../supabase/functions/_shared/domain/format.ts';
import type { ClientOutletContext } from './ClientLayout';

const ICONS = { shield: ShieldCheck, clock: Clock, sparkle: Sparkle, car: Car, drop: Drop, wrench: Wrench, star: Star, medal: Medal } as const;

export function hoursSummary(hours: Array<{ isodow: number; opens: string; closes: string }>): Array<{ days: string; time: string }> {
  const byDay = [1, 2, 3, 4, 5, 6, 7].map((d) => hours.filter((h) => h.isodow === d).map((h) => `${h.opens}–${h.closes}`).join(', ') || 'выходной');
  const out: Array<{ days: string; time: string }> = [];
  let start = 1;
  for (let d = 2; d <= 8; d++) {
    if (d === 8 || byDay[d - 1] !== byDay[start - 1]) {
      out.push({ days: start === d - 1 ? WEEKDAY_NAMES[start]! : `${WEEKDAY_NAMES[start]}–${WEEKDAY_NAMES[d - 1]}`, time: byDay[start - 1]! });
      start = d;
    }
  }
  return out;
}

export function HomePage() {
  const { sf, openBooking } = useTenant();
  const { openAssistant } = useOutletContext<ClientOutletContext>();
  const p = sf.profile;
  const hero = imageUrl(sf, p.hero_path);
  const logo = imageUrl(sf, p.logo_path);
  const featured = sf.services.slice(0, 4);

  return (
    <VStack gap={0}>
      <header className="hero safe-top">
        {hero ? <img className="hero-img" src={hero} alt={p.hero_alt} fetchPriority="high" decoding="async" /> : null}
        <VStack gap={3} padding={4} paddingBlockEnd={6} className="page">
          <HStack gap={2} vAlign="center">
            {logo ? <img src={logo} alt="" width={40} height={40} style={{ borderRadius: 12, objectFit: 'cover' }} /> : null}
            {sf.tenant.status === 'preview' ? <Badge label="Демо" variant="warning" /> : null}
          </HStack>
          <Heading level={1} type="display-2">{p.name}</Heading>
          <Text type="large" color="secondary">{p.tagline}</Text>
          {/* the CTA lives inside the hero, so it scrolls together with the photo */}
          <button type="button" className="glass-cta" onClick={() => openBooking()}>
            <CalendarPlus size={22} weight="bold" aria-hidden />
            <span>Записаться</span>
          </button>
        </VStack>
      </header>

      <VStack gap={8} padding={4} className="page">
        <Reveal label="О студии">
          <VStack gap={4}>
            <Text>{p.description}</Text>
            <Grid columns={{ minWidth: 200 }} gap={2}>
              {p.info_cards.map((c, i) => {
                const I = ICONS[c.icon as keyof typeof ICONS] ?? Sparkle;
                return (
                  <Card key={i} padding={4} variant="muted">
                    <VStack gap={2}>
                      <I size={24} color="var(--color-accent)" aria-hidden />
                      <Text weight="semibold">{c.title}</Text>
                      <Text type="supporting">{c.text}</Text>
                    </VStack>
                  </Card>
                );
              })}
            </Grid>
          </VStack>
        </Reveal>

        <Reveal id="booking" label="Запись в студию">
          <VStack gap={3}>
            <Heading level={2}>Запись в студию</Heading>
            <Card padding={0}>
              <List hasDividers>
                {featured.map((s) => (
                  <ListItem key={s.id} label={s.name}
                    description={`${formatDuration(s.duration_minutes)} · ${formatMoney(s.price_cents, sf.tenant.currency, s.price_is_from)}`}
                    endContent={<Button label="Выбрать" size="sm" variant="secondary" onClick={() => openBooking(s.id)} />} />
                ))}
              </List>
            </Card>
            <HStack gap={2} wrap="wrap">
              <Button label="Выбрать время" variant="primary" size="lg" icon={<CalendarPlus size={18} />} onClick={() => openBooking()} />
              {p.assistant_enabled ? <Button label="Спросить помощника" variant="ghost" size="lg" icon={<Sparkle size={18} />} onClick={openAssistant} /> : null}
            </HStack>
          </VStack>
        </Reveal>

        {sf.gallery.length > 0 ? (
          <Reveal label="Наши работы">
            <VStack gap={3}>
              <Heading level={2}>Наши работы</Heading>
              <div className="h-scroll" role="list">
                {sf.gallery.map((g) => (
                  <figure key={g.id} role="listitem" style={{ width: 'min(78vw, 320px)', margin: 0 }}>
                    <div className="round-media" style={{ aspectRatio: '4 / 3' }}>
                      <img className="media" src={imageUrl(sf, g.image_path)} alt={g.caption} loading="lazy" decoding="async" />
                    </div>
                    <figcaption><Text type="supporting">{g.caption}</Text></figcaption>
                  </figure>
                ))}
              </div>
            </VStack>
          </Reveal>
        ) : null}

        <Reveal label="Контакты">
          <VStack gap={3}>
            <Heading level={2}>Как нас найти</Heading>
            <Card padding={4}>
              <VStack gap={3}>
                <HStack gap={2} vAlign="start"><MapPin size={20} aria-hidden /><Text>{p.address}</Text></HStack>
                {p.how_to_find ? <Text type="supporting">{p.how_to_find}</Text> : null}
                <VStack gap={1}>
                  {hoursSummary(sf.hours).map((h) => (
                    <HStack key={h.days} hAlign="between"><Text type="supporting">{h.days}</Text><Text className="tabular">{h.time}</Text></HStack>
                  ))}
                </VStack>
                <HStack gap={2} wrap="wrap">
                  <Button label="Позвонить" icon={<Phone size={18} />} variant="secondary" href={`tel:${p.phone}`} />
                  {p.map_url ? <Button label="Маршрут" icon={<NavigationArrow size={18} />} variant="secondary" href={p.map_url} target="_blank" /> : null}
                </HStack>
              </VStack>
            </Card>
          </VStack>
        </Reveal>
      </VStack>
    </VStack>
  );
}
